"use client";

import { useEffect, useState } from "react";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { useAppAuth } from "@/hooks/useAppAuth";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api from "../http";
import { toast } from "react-toastify";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useI18n } from "@/contexts/I18nContext";

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
  role: string | null;
  tools: string[];
  created_at: string;
}

export default function Users() {
  const { getAccessToken, user, role: currentUserRole } = useAppAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState<User[]>([]);
  const [expandedToolsByUser, setExpandedToolsByUser] = useState<Record<string, boolean>>({});

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userFilter, setUserFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const renderToolBadges = (
    userId: string,
    tools: string[] | null | undefined,
  ) => {
    if (!tools || tools.length === 0) {
      return <span className="text-xs text-gray-400">{t("users.noCategories")}</span>;
    }

    const isExpanded = expandedToolsByUser[userId];
    const visibleTools = isExpanded ? tools : tools.slice(0, 2);

    return (
      <div className="flex flex-wrap gap-1">
        {visibleTools.map((tool) => (
          <span
            key={tool}
            className="inline-block max-w-full truncate rounded-full px-2 py-1 text-xs font-medium"
            style={{
              backgroundColor: `${APP_COLOR}15`,
              color: APP_COLOR,
            }}
            title={tool}
          >
            {tool}
          </span>
        ))}
        {tools.length > 2 && (
          <button
            type="button"
            className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200"
            onClick={() =>
              setExpandedToolsByUser((prev) => ({
                ...prev,
                [userId]: !prev[userId],
              }))
            }
          >
            {isExpanded ? t("users.less") : `+${tools.length - 2}`}
          </button>
        )}
      </div>
    );
  };

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });

      // Also update the user in the users list if they exist there
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === currentUser.id
            ? { ...user, profile_picture_url: imageUrl }
            : user,
        ),
      );
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);

    // Also update the user in the users list if they exist there
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === updatedUser.id
          ? {
              ...user,
              first_name: updatedUser.first_name || user.first_name,
              last_name: updatedUser.last_name || user.last_name,
              profile_picture_url: (updatedUser.profile_image_url ?? null) as
                | string
                | null,
            }
          : user,
      ),
    );
  };


  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const offset = (currentPage - 1) * 10;
      const data = await api.getUsers(token, offset, 10, userFilter, sortField, sortDirection);

      if (data && data.users && data.users.data) {
        setUsers(data.users.data);
        setTotalPages(data.users.total_pages || 1);
        setTotalUsers(data.users.total_users || 0);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(t("users.fetchFailed"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, userFilter, sortField, sortDirection]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      // Check if this is the last admin and trying to change to member
      const adminCount = users.filter(u => u.role === "admin").length;
      const userBeingChanged = users.find(u => u.id === userId);
      
      if (userBeingChanged?.role === "admin" && newRole === "member" && adminCount === 1) {
        toast.error(t("users.lastAdminError"), {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        return;
      }

      const token = await getAccessToken();
      await api.updateUserRole(userId, newRole, token);
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      
      toast.success(t("users.roleChanged"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error: any) {
      console.error("Error updating user role:", error);
      
      // Check for specific error messages
      const errorMessage = error.message?.includes("Cannot change your own role")
        ? t("users.changeOwnRoleError")
        : t("users.changeRoleFailed");
      
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold">{t("users.title")}</h1>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto">
          {/* Mobile Header - only show on mobile */}
          <div className="lg:hidden mb-6">
            <h1 className="text-xl font-semibold mb-4">{t("users.title")}</h1>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-medium">{t("users.allUsers")}</span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className={`font-bold text-xs`}
                  style={{ color: APP_COLOR }}
                >
                  {t(totalUsers === 1 ? "users.count_one" : "users.count_other", { count: totalUsers })}
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium">{t("users.allUsers")}</span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className={`font-bold text-xs`}
                  style={{ color: APP_COLOR }}
                >
                  {t(totalUsers === 1 ? "users.count_one" : "users.count_other", { count: totalUsers })}
                </span>
              </div>
            </div>

            <div className="relative w-[300px]">
              <input
                type="text"
                placeholder={t("users.searchPlaceholder")}
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setUserFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Mobile Search - only show on mobile */}
          <div className="lg:hidden mb-4">
            <div className="relative w-full">
              <input
                type="text"
                placeholder={t("users.searchPlaceholder")}
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setUserFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Mobile User Cards */}
          <div className="lg:hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                {t("users.loading")}
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t("users.empty")}
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-3 mb-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage
                            src={user.profile_picture_url || undefined}
                            alt="User avatar"
                          />
                          <AvatarFallback>
                            {user.first_name && user.last_name
                              ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                              : user.first_name?.charAt(0)?.toUpperCase() ||
                                user.last_name?.charAt(0)?.toUpperCase() ||
                                user.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                          <div className="mt-1">
                            <select
                              value={user.role || "member"}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              disabled={user.id === currentUser?.id}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
                                user.role === "superadmin"
                                  ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                                  : user.role === "admin" 
                                    ? "bg-red-100 text-red-800 hover:bg-red-200" 
                                    : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                              } ${user.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <option value="member" className="bg-white text-black">Mitglied</option>
                              <option value="admin" className="bg-white text-black">Administrator</option>
                              {currentUserRole === "superadmin" && (
                                <option value="superadmin" className="bg-white text-black">Superadmin</option>
                              )}
                            </select>
                          </div>
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Hinzugefügt:{" "}
                      {user.created_at
                        ? new Date(user.created_at).toLocaleString("de-DE", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "N/A"}
                    </div>
                    <div className="mt-3">
                      <div className="mb-1 text-xs font-medium text-gray-500">
                        Kategorien
                      </div>
                      {renderToolBadges(user.id, user.tools)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table Container */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[1.6fr_2fr_2fr_1fr_1.6fr] bg-gray-50 text-gray-700 border-b border-gray-200">
              <div className="p-4 font-medium flex items-center gap-1">
                Name <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'name' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'name' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Email <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'email' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'email' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                {t("users.role")} <ChevronDown size={16} className="text-gray-400" />
              </div>
              <div className="p-4 font-medium">
                {t("users.categories")}
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                {t("users.addedOn")}{" "}
                <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'date' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'date' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
            </div>

            {/* Table Rows */}
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                {t("users.loading")}
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t("users.empty")}
              </div>
            ) : (
              users.map((user) => (
                <div
                  key={user.id}
                  className="grid grid-cols-[1.6fr_2fr_2fr_1fr_1.6fr] border-b border-gray-200"
                >
                  <div className="p-4 flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage
                        src={user.profile_picture_url || undefined}
                        alt="User avatar"
                      />
                      <AvatarFallback>
                        {user.first_name && user.last_name
                          ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                          : user.first_name?.charAt(0)?.toUpperCase() ||
                            user.last_name?.charAt(0)?.toUpperCase() ||
                            user.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {user.first_name} {user.last_name}
                    </span>
                  </div>
                  <div className="p-4 flex items-start break-all overflow-hidden">{user.email}</div>
                  <div className="p-4 flex items-center">
                    <select
                      value={user.role || "member"}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={user.id === currentUser?.id}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
                        user.role === "superadmin"
                          ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                          : user.role === "admin" 
                            ? "bg-red-100 text-red-800 hover:bg-red-200" 
                            : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                      } ${user.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="member" className="bg-white text-black">Mitglied</option>
                      <option value="admin" className="bg-white text-black">Administrator</option>
                      {currentUserRole === "superadmin" && (
                        <option value="superadmin" className="bg-white text-black">Superadmin</option>
                      )}
                    </select>
                  </div>
                  <div className="p-4 flex items-center">
                    {renderToolBadges(user.id, user.tools)}
                  </div>
                  <div className="p-4 flex items-center text-gray-600">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString("en-US", {
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "N/A"}
                  </div>
                </div>
              ))
            )}

            {/* Pagination (Within Table) */}
            <div className="flex items-center justify-between p-4 bg-gray-50">
              {/* Left side: arrows + page numbers */}
              <div className="flex items-center gap-5">
                {/* Previous button */}
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
                >
                  &lt; Zurück
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-3">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <span
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`cursor-pointer ${
                          page === currentPage
                            ? "w-8 h-8 flex items-center justify-center rounded-full"
                            : "text-gray-700 hover:text-gray-900"
                        }`}
                        style={
                          page === currentPage
                            ? {
                                color: APP_COLOR,
                                backgroundColor: `${APP_COLOR}20`,
                              }
                            : undefined
                        }
                      >
                        {page}
                      </span>
                    ),
                  )}
                </div>

                {/* Next button */}
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
                >
                  Weiter &gt;
                </button>
              </div>

              {/* Right side: "Showing x of y results" */}
              <div className="text-sm text-gray-500">
                Zeige {users.length} von {totalUsers} Ergebnissen
              </div>
            </div>
          </div>

          {/* Mobile Pagination */}
          <div className="lg:hidden mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  &lt; Zurück
                </button>
                <span className="text-sm text-gray-500">
                  Seite {currentPage} von {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  Weiter &gt;
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {users.length} von {totalUsers}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
